import React, { useEffect, useContext } from "react";
import {
  GridContainer,
  GridItem,
  PeriodicBillingReportFormRootArea,
} from "./PeriodicBillingReport.styles";
import {
  periodicBillingReportInitialValues,
  periodicBillingReportValidationSchema,
} from "./PeriodicBillingReport.resources";
import { useFormik } from "formik";
import TextField from "../../textField/TextField";
import DateField from "../../dateField/DateField";
import SelectField from "../../selectField/SelectField";
import { Button, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { generateBillingReport } from "./PeriodicBillingReport.thunk";
import AuthContext from "../../../context/AuthContext";
import PeriodicBillingReportResultModal from "./PeriodicBillingReport.resultModal";
import moment from "moment";
import { useModal } from "../../modal";
import { useSelector } from "react-redux";

const PeriodicBillingReport = () => {
  const dispatch = useDispatch();
  const { token, acquirerData } = useContext(AuthContext);
  const merchantID = acquirerData?.merchantID || "";
  const { open } = useModal();
  const { status } = useSelector((state) => state.periodicBillingReport);

  const handleOpenResultModal = () => {
    open({
      title: `Billing Range Report from ${moment(values.start_date).format(
        "MM/DD/YYYY"
      )} to ${moment(values.end_date).format("MM/DD/YYYY")}`,
      maxWidth: `lg`,
      fullWidth: true,
    });
  };

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleChange,
    handleBlur,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormik({
    initialValues: {
      ...periodicBillingReportInitialValues,
      merchant_id: merchantID,
    },
    validationSchema: periodicBillingReportValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: (values) => {
      setSubmitting(true);
      dispatch(
        generateBillingReport({
          merchant_id: values.merchant_id,
          start_date: values.start_date,
          end_date: values.end_date,
          status: values.status || undefined,
          token: token,
          onSuccessCallback: handleOpenResultModal,
        })
      );
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (merchantID && values.merchant_id !== merchantID) {
      setFieldValue("merchant_id", merchantID, false);
    }
  }, [merchantID, setFieldValue, values.merchant_id]);

  useEffect(() => {
    // Force validation when date values change
    if (values.start_date || values.end_date) {
      validateForm();
    }
  }, [values, validateForm]);

  return (
    <PeriodicBillingReportFormRootArea>
      <GridContainer component="form" onSubmit={handleSubmit}>
        <PeriodicBillingReportResultModal />
        <GridItem xs={12}>
          <Typography variant="h6" sx={{ mb: 3, mt: 2 }}>
            Billing Range Report
          </Typography>
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <TextField
            id="merchant_id"
            name="merchant_id"
            fullWidth
            label="Merchant ID"
            value={values.merchant_id}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled
            error={errors.merchant_id}
            touched={touched.merchant_id}
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <DateField
            id="start_date"
            name="start_date"
            fullWidth
            label="Start Date"
            showTimezoneLabel
            value={values.start_date}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            required
            error={errors.start_date}
            touched={touched.start_date}
            minDate={moment().subtract(540, "days")}
            maxDate={moment()}
            helperText="Select a date within the last 90 days"
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <DateField
            id="end_date"
            name="end_date"
            fullWidth
            label="End Date"
            showTimezoneLabel
            value={values.end_date}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            required
            error={errors.end_date}
            touched={touched.end_date}
            minDate={moment().subtract(540, "days")}
            maxDate={moment()}
            helperText="Select a date within the last 90 days"
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <SelectField
            id="status"
            name="status"
            fullWidth
            label="Status (optional)"
            value={values.status || ""}
            options={[
              { value: "", label: "All" },
              { value: "P", label: "Pending" },
              { value: "E", label: "Executed" },
              { value: "C", label: "Canceled" },
            ]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.status}
            touched={touched.status}
          />
        </GridItem>
        <GridContainer>
          <GridItem xs={12} sm={6} lg={4} padding="15px 15px 15px 0">
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={isSubmitting || status === "loading"}
              onClick={handleSubmit}
            >
              Generate Report
            </Button>
          </GridItem>
        </GridContainer>
      </GridContainer>
    </PeriodicBillingReportFormRootArea>
  );
};

export default PeriodicBillingReport;

